import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, message, Modal, Button, Space, Tag, Typography, Image, Spin } from 'antd'
import {
  SendOutlined,
  EditOutlined,
  DeleteOutlined,
  InstagramOutlined,
  FacebookOutlined,
  TwitterOutlined,
  YoutubeOutlined,
  EyeOutlined,
} from '@ant-design/icons'
import {
  Calendar,
  type CalendarEvent,
  type AppointmentStatus,
  type AppointmentType,
} from '@perfectamed/calendar'
import '@perfectamed/calendar/styles.css'

const { Text, Paragraph } = Typography

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3520/api'

type SocialPlatform = 'INSTAGRAM' | 'FACEBOOK' | 'TIKTOK' | 'TWITTER' | 'YOUTUBE'
type SocialPostStatus = 'DRAFT' | 'SCHEDULED' | 'PUBLISHING' | 'PUBLISHED' | 'FAILED' | 'CANCELLED'

interface SocialPost {
  id: string
  content: string
  mediaUrls: string[]
  thumbnailUrl: string | null
  platforms: SocialPlatform[]
  scheduledAt: string | null
  publishedAt: string | null
  status: SocialPostStatus
  createdAt: string
}

// Map social post status to calendar appointment status
const STATUS_MAP: Record<SocialPostStatus, AppointmentStatus> = {
  DRAFT: 'SCHEDULED',
  SCHEDULED: 'CONFIRMED',
  PUBLISHING: 'IN_PROGRESS',
  PUBLISHED: 'COMPLETED',
  FAILED: 'CANCELLED',
  CANCELLED: 'NO_SHOW',
}

// Platform icons
const platformIcons: Record<SocialPlatform, React.ReactNode> = {
  INSTAGRAM: <InstagramOutlined style={{ color: '#E4405F' }} />,
  FACEBOOK: <FacebookOutlined style={{ color: '#1877F2' }} />,
  TIKTOK: <span style={{ fontWeight: 'bold', fontSize: 12 }}>TT</span>,
  TWITTER: <TwitterOutlined style={{ color: '#000' }} />,
  YOUTUBE: <YoutubeOutlined style={{ color: '#FF0000' }} />,
}

const statusLabels: Record<SocialPostStatus, { label: string; color: string }> = {
  DRAFT: { label: 'Nacrt', color: 'default' },
  SCHEDULED: { label: 'Zakazano', color: 'blue' },
  PUBLISHING: { label: 'Objavljuje se', color: 'processing' },
  PUBLISHED: { label: 'Objavljeno', color: 'success' },
  FAILED: { label: 'Neuspelo', color: 'error' },
  CANCELLED: { label: 'Otkazano', color: 'warning' },
}

const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('admin_access_token')
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

export function SocialMediaCalendarPage() {
  const navigate = useNavigate()
  const [posts, setPosts] = useState<SocialPost[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedPost, setSelectedPost] = useState<SocialPost | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Fetch scheduled posts
  const fetchPosts = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API_URL}/admin/social-media/posts?status=SCHEDULED&pageSize=200`, {
        headers: getAuthHeaders(),
      })
      if (!res.ok) throw new Error('Failed to fetch posts')
      const json = await res.json()
      setPosts(json.data || [])
    } catch (error) {
      console.error('Error fetching posts:', error)
      message.error('Greska pri ucitavanju postova')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchPosts()
  }, [fetchPosts])

  // Convert social posts to calendar events
  const events: CalendarEvent[] = useMemo(() => {
    return posts
      .filter((post) => post.scheduledAt)
      .map((post) => {
        const start = new Date(post.scheduledAt!)
        const end = new Date(start.getTime() + 30 * 60000) // 30 min duration

        return {
          id: post.id,
          title: post.content.substring(0, 50) + (post.content.length > 50 ? '...' : ''),
          start,
          end,
          duration: 30,
          status: STATUS_MAP[post.status] || 'SCHEDULED',
          type: 'INITIAL_CONSULTATION' as AppointmentType, // Required but unused
          doctor: {
            id: 'social',
            name: post.platforms.join(', '),
            title: '',
          },
          patient: {
            id: post.id,
            name: post.platforms.map((p) => p.charAt(0)).join(''),
          },
        }
      })
  }, [posts])

  // Handle event click
  const handleEventClick = (event: CalendarEvent) => {
    const post = posts.find((p) => p.id === event.id)
    if (post) {
      setSelectedPost(post)
      setIsModalOpen(true)
    }
  }

  // Handle slot click - navigate to create page with pre-filled date
  const handleSlotClick = (date: Date, hour: number, minute: number) => {
    const scheduledDate = new Date(date)
    scheduledDate.setHours(hour, minute, 0, 0)
    navigate(`/social-media/create?scheduledAt=${scheduledDate.toISOString()}`)
  }

  // Handle event drop - reschedule post
  const handleEventDrop = async (event: CalendarEvent, newStart: Date) => {
    try {
      const res = await fetch(`${API_URL}/admin/social-media/posts/${event.id}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          scheduledAt: newStart.toISOString(),
        }),
      })

      if (!res.ok) {
        throw new Error('Failed to reschedule')
      }

      // Update local state
      setPosts((prev) =>
        prev.map((p) =>
          p.id === event.id ? { ...p, scheduledAt: newStart.toISOString() } : p
        )
      )
      message.success('Post je premesten')
    } catch (error) {
      message.error('Greska pri pomeranju posta')
      fetchPosts()
    }
  }

  // Publish post now
  const handlePublishNow = async () => {
    if (!selectedPost) return

    try {
      const res = await fetch(`${API_URL}/admin/social-media/posts/${selectedPost.id}/publish`, {
        method: 'POST',
        headers: getAuthHeaders(),
      })

      if (!res.ok) throw new Error('Failed to publish')

      message.success('Post je poslat na objavljivanje')
      setIsModalOpen(false)
      fetchPosts()
    } catch (error) {
      message.error('Greska pri objavljivanju')
    }
  }

  if (loading && posts.length === 0) {
    return (
      <Card title="Kalendar postova">
        <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}>
          <Spin size="large" />
        </div>
      </Card>
    )
  }

  return (
    <Card
      title="Kalendar postova"
      extra={
        <Space>
          <Text type="secondary">{posts.length} zakazanih postova</Text>
          <Button type="primary" onClick={() => navigate('/social-media/create')}>
            Novi post
          </Button>
        </Space>
      }
      styles={{ body: { padding: 0 } }}
    >
      <Calendar
        events={events}
        defaultView="week"
        locale="sr"
        editable={true}
        startHour={6}
        endHour={23}
        slotDuration={30}
        onEventClick={handleEventClick}
        onSlotClick={handleSlotClick}
        onEventDrop={handleEventDrop}
      />

      {/* Post Detail Modal */}
      <Modal
        title="Detalji posta"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={
          <Space>
            <Button onClick={() => setIsModalOpen(false)}>Zatvori</Button>
            <Button
              icon={<EyeOutlined />}
              onClick={() => {
                setIsModalOpen(false)
                navigate(`/social-media/${selectedPost?.id}`)
              }}
            >
              Pogledaj
            </Button>
            <Button
              icon={<EditOutlined />}
              onClick={() => {
                setIsModalOpen(false)
                navigate(`/social-media/${selectedPost?.id}/edit`)
              }}
            >
              Izmeni
            </Button>
            {selectedPost?.status === 'SCHEDULED' && (
              <Button type="primary" icon={<SendOutlined />} onClick={handlePublishNow}>
                Objavi odmah
              </Button>
            )}
          </Space>
        }
        width={500}
      >
        {selectedPost && (
          <Space direction="vertical" style={{ width: '100%' }} size={16}>
            {/* Thumbnail */}
            {selectedPost.thumbnailUrl && (
              <Image
                src={selectedPost.thumbnailUrl}
                alt="Post thumbnail"
                style={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 8 }}
              />
            )}

            {/* Content */}
            <Paragraph
              ellipsis={{ rows: 4, expandable: true }}
              style={{ marginBottom: 0 }}
            >
              {selectedPost.content}
            </Paragraph>

            {/* Platforms */}
            <Space wrap>
              {selectedPost.platforms.map((platform) => (
                <Tag key={platform}>
                  {platformIcons[platform]} {platform}
                </Tag>
              ))}
            </Space>

            {/* Status and time */}
            <Space split={<span style={{ color: '#d9d9d9' }}>|</span>}>
              <Tag color={statusLabels[selectedPost.status].color}>
                {statusLabels[selectedPost.status].label}
              </Tag>
              {selectedPost.scheduledAt && (
                <Text type="secondary">
                  {new Date(selectedPost.scheduledAt).toLocaleString('sr-Latn-RS', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Text>
              )}
            </Space>
          </Space>
        )}
      </Modal>
    </Card>
  )
}

export default SocialMediaCalendarPage
